import {
  BoltonDetailsBase,
  BoltonPlan,
  BoltonPlanStatus,
  BoltonProductType,
  GetSignupResponse,
  VOLVO_APP_LINK,
  HYUNDAI_CAR_APP_LINK,
  KIA_CAR_APP_LINK,
  PlanDetails,
  KaluzaVehicleDevice,
  BoltonTitle,
  connectedVehicleIntegrationType,
  noConnectedDeviceWithVolvoSignUp,
  OVO_CHARGE_APP_LINK,
} from '@monovo/ev-anytime';
import { P, Stack, Strong, theme } from '@ovotech/element-v5';
import React from 'react';
import { LinkStyled } from '../components/ChargeAnytimeBolton/ChargeAnytimeBolton.styled';
import styled from 'styled-components';

const Bold = styled(Strong)`
  color: ${theme.color.surface.onSurface};
  /* font-weight is already applied by Strong, so no need to add it */
`;

const isPayg = (boltonProductType: BoltonProductType): boolean =>
  boltonProductType === 'PAYD';

const PARTNER_CONFIG: Record<string, { name: string; link: string }> = {
  Hyundai: { name: 'Hyundai', link: HYUNDAI_CAR_APP_LINK },
  Kia: { name: 'Kia', link: KIA_CAR_APP_LINK },
};

const getPartnerName = (
  signupIntegrationType?: GetSignupResponse['integrationType'],
) =>
  (signupIntegrationType && PARTNER_CONFIG[signupIntegrationType]?.name) || '';

const getPartnerAppLink = (
  signupIntegrationType?: GetSignupResponse['integrationType'],
) =>
  (signupIntegrationType && PARTNER_CONFIG[signupIntegrationType]?.link) || '';

const getDynamicPlanTitle = (
  plan: PlanDetails,
  partnerName: string,
  isPaygPlan: boolean,
): BoltonTitle => {
  if (isPaygPlan) return 'Pay as you go';
  if (partnerName && plan.id === 'paym-oem-tier-one') {
    return `${partnerName} ${plan.title}` as BoltonTitle;
  }
  return plan.title;
};

const PartnerAppLink = ({
  href,
  name,
}: {
  href: string;
  name: string;
}): React.ReactElement => (
  <LinkStyled
    href={href}
    style={{ fontSize: theme.font.size.md.mediaQuery.sm }}
    target="_blank"
  >
    {name} app
  </LinkStyled>
);

export const getChargeAnytimePlanCardDetails = (
  allPlans: PlanDetails[],
  boltonPlan: BoltonPlan,
  vehicles: KaluzaVehicleDevice[],
  signupIntegrationType?: GetSignupResponse['integrationType'],
): { title: BoltonTitle; miles: string; text: React.ReactNode } | null => {
  const { status } = boltonPlan;

  const getChargeAnytimePlanDetails = (bolton: BoltonDetailsBase | null) => {
    if (!bolton) return null;
    return allPlans.find(plan => plan.boltonProductType === bolton.productType);
  };

  const partnerName = getPartnerName(signupIntegrationType);
  const partnerAppLink = getPartnerAppLink(signupIntegrationType);
  const isPartner = Boolean(partnerName);

  switch (status) {
    case BoltonPlanStatus.Active: {
      const { productType } = boltonPlan.current;
      const planDetails = getChargeAnytimePlanDetails(boltonPlan.current);
      if (!planDetails) return null;

      const payg = isPayg(productType);
      const vehicleIntegrationType = connectedVehicleIntegrationType(vehicles);

      const paygVolvoProduct = allPlans?.find(
        (plan: PlanDetails) => plan.id === 'payg-volvo',
      );

      return {
        title: getDynamicPlanTitle(planDetails, partnerName, payg),
        miles: planDetails.miles,
        text:
          (vehicleIntegrationType === 'deviceManufacturer' ||
            noConnectedDeviceWithVolvoSignUp(
              vehicleIntegrationType,
              signupIntegrationType,
            )) &&
          payg ? (
            <Stack spaceBetween={4}>
              <P data-testid="volvo-app-message">
                Enjoy pay as you go at half our standard rate
                {paygVolvoProduct?.price
                  ? `, so just ${paygVolvoProduct?.price}/kWh.`
                  : '.'}
              </P>
              <P>
                This offer is available until we can make monthly plans
                available for Volvo drivers.
              </P>
              <P>
                We’ll let you know when monthly plans will become available –
                it’ll be easy to switch.
              </P>
            </Stack>
          ) : isPartner && payg ? (
            <P>
              Driving more than usual?{' '}
              <Bold>Switch to our {partnerName} monthly plan</Bold>.
            </P>
          ) : isPartner ? (
            <P>
              Driving less than usual? <Bold>Switch plans</Bold> anytime to suit
              your usage.
            </P>
          ) : payg ? (
            <P>
              Want more predictability? <Bold>Switch to our monthly plans</Bold>{' '}
              anytime.
            </P>
          ) : (
            <P>
              Driving more or less? <Bold>Switch plans</Bold> anytime to suit
              your usage.
            </P>
          ),
      };
    }

    case BoltonPlanStatus.Cancelled: {
      const { productType } = boltonPlan.current;
      const planDetails = getChargeAnytimePlanDetails(boltonPlan.current);
      if (!planDetails) return null;
      const payg = isPayg(productType);

      return {
        title: getDynamicPlanTitle(planDetails, partnerName, payg),
        miles: planDetails.miles,
        text: payg ? (
          <P>
            You've cancelled. Your plan will stop <Bold>at midnight</Bold>.
          </P>
        ) : (
          <P>
            You've cancelled. Your plan will stop{' '}
            <Bold>at the end of the month</Bold>.
          </P>
        ),
      };
    }

    case BoltonPlanStatus.Switching: {
      const currentPlanDetails = getChargeAnytimePlanDetails(
        boltonPlan.current,
      );
      const futurePlanDetails = getChargeAnytimePlanDetails(boltonPlan.future);

      if (!currentPlanDetails || !futurePlanDetails) return null;

      const payg = isPayg(boltonPlan.current.productType);
      const futurePayg = isPayg(boltonPlan.future.productType);

      return {
        title: getDynamicPlanTitle(currentPlanDetails, partnerName, payg),
        miles: currentPlanDetails.miles,
        text: (
          <P>
            You've switched! Your new plan,{' '}
            <Bold>
              {getDynamicPlanTitle(futurePlanDetails, partnerName, futurePayg)}
            </Bold>
            {!futurePayg ? ` (${futurePlanDetails.miles})` : ''}, starts{' '}
            {payg ? 'tomorrow' : 'next month'}.
          </P>
        ),
      };
    }

    case BoltonPlanStatus.Initialized: {
      const planDetails = getChargeAnytimePlanDetails(boltonPlan.future);
      if (!planDetails) return null;

      const payg = isPayg(boltonPlan.future.productType);
      const isVolvo = signupIntegrationType === 'Volvo';

      return {
        title: getDynamicPlanTitle(planDetails, partnerName, payg),
        miles: planDetails.miles,
        text: isPartner ? (
          <Stack spaceBetween={4}>
            <P
              data-testid={`${partnerName.toLowerCase()}-app-message-initialised`}
            >
              To activate <Bold>Charge Anytime</Bold>, go to your{' '}
              <PartnerAppLink href={partnerAppLink} name={partnerName} /> and
              link your <Bold>{partnerName}</Bold> and <Bold>OVO account</Bold>.
            </P>
            <P>
              Once you've done this, you'll be able to manage your{' '}
              <Bold>{partnerName}</Bold> smart charging directly from your{' '}
              <Bold>{partnerName} app</Bold>.
            </P>
          </Stack>
        ) : isVolvo ? (
          <Stack spaceBetween={4}>
            <P data-testid="volvo-app-message-initialised">
              To activate <Bold>Charge Anytime</Bold>, go to your{' '}
              <LinkStyled
                href={VOLVO_APP_LINK}
                style={{ fontSize: theme.font.size.md.mediaQuery.sm }}
                target="_blank"
              >
                Volvo Cars app
              </LinkStyled>{' '}
              and link your <Bold>Volvo</Bold> and <Bold>OVO account</Bold>.
            </P>
            <P>
              Once you've done this, you'll be able to manage your{' '}
              <Bold>Volvo</Bold> smart charging directly from your{' '}
              <Bold>Volvo Cars app</Bold>.
            </P>
          </Stack>
        ) : (
          <Stack spaceBetween={4}>
            <P data-testid="charge-anytime-app-message">
              You can activate <Bold>Charge Anytime</Bold> by setting up smart
              charging through the{' '}
              <LinkStyled
                href={OVO_CHARGE_APP_LINK}
                data-testid="ovo-charge-app-link-initialised-android"
                target="_blank"
                style={{ fontSize: theme.font.size.md.mediaQuery.sm }}
              >
                OVO Charge app
              </LinkStyled>
              . Just open the <Bold>OVO Charge app</Bold>, link your{' '}
              <Bold>OVO account</Bold> and follow the steps to connect your home
              charger or EV.
            </P>
          </Stack>
        ),
      };
    }
    default:
      return null;
  }
};

export const canCancelChargeAnytime = (boltonPlan: BoltonPlan) => {
  if (!boltonPlan) {
    return false;
  }
  switch (boltonPlan.status) {
    case BoltonPlanStatus.Active:
      return true;
    case BoltonPlanStatus.Cancelled:
      return false;
    case BoltonPlanStatus.Switching:
      return true;
    case BoltonPlanStatus.Initialized:
      return true;
  }
};

export const canReinstateChargeAnytime = (boltonPlan: BoltonPlan) => {
  if (!boltonPlan) {
    return false;
  }
  return boltonPlan.status === BoltonPlanStatus.Cancelled;
};

export const canSwitchChargeAnytime = (
  boltonPlan: BoltonPlan,
  vehicles: KaluzaVehicleDevice[],
  signupIntegrationType?: GetSignupResponse['integrationType'],
) => {
  const vehicleIntegrationType = connectedVehicleIntegrationType(vehicles);

  // Prevent switching for Volvo direct integrations (identified by `hasVolvoIntegration` using Kaluza data)
  // as Volvo customers currently are only allowed on the PAYG bolton
  // The plan status check below handles other ineligible states (`Initialized`, `Cancelled`, `Switching`).
  // Note: `signUpIntegrationType === Volvo` isn't relevant here because the flag is used to determine whether
  // a customer is a Volvo customer or for 'Initialized' status but all 'Initialized' customers are prevented
  // from switching so no need for an extra check
  if (!boltonPlan || vehicleIntegrationType === 'deviceManufacturer') {
    return false;
  }
  switch (boltonPlan.status) {
    case BoltonPlanStatus.Active:
      if (
        noConnectedDeviceWithVolvoSignUp(
          vehicleIntegrationType,
          signupIntegrationType,
        ) &&
        isPayg(boltonPlan.current.productType)
      ) {
        return false;
      }
      return true;
    case BoltonPlanStatus.Cancelled:
      return false;
    case BoltonPlanStatus.Switching:
      return false;
    case BoltonPlanStatus.Initialized:
      return false;
  }
};
